/* eslint-disable @typescript-eslint/no-explicit-any */
import { SearchProvider } from '@faststore/sdk'
import { graphql } from 'gatsby'
import { GatsbySeo } from 'gatsby-plugin-next-seo'
import ProductGallery from 'src/components/sections/ProductGalleryV2'
import { ITEMS_PER_PAGE } from 'src/constants'
import { applySearchState } from 'src/sdk/search/state'
import { Helmet } from 'react-helmet-async'
import { mark } from 'src/sdk/tests/mark'
import type { PageProps } from 'gatsby'
import type {
  SearchPageQueryQuery,
  SearchPageQueryQueryVariables,
} from '@generated/graphql'
import type { SearchState } from '@faststore/sdk'
import type { SearchResult } from 'src/components/sections/ProductGalleryV2/types'
import { getCMSPageByContentType } from 'src/utils/getCMSPageDataByContentType'
import MainBannerBrands from 'src/components/brands/mainBanner/mainBanner'
import SectionBrand from 'src/components/brands/sectionBrand/sectionBrand'
import { MusePlpEntryPoint } from 'src/components/muse/MusePlpEntryPoint'
import './styles.scss'
import getSearchServerSidedata from 'src/utils/plp/getSearchServerSidedata'
import { handleSSRError } from 'src/utils/handleSSRError'
import CollectionPLPTemplate from 'src/components/cms/templates/CollectionPLPTemplate'
import CampanhaTemplate from 'src/components/cms/templates/CampanhaTemplate'
import type { CollectionPLPTemplateProps } from 'src/components/cms/templates/CollectionPLPTemplate'
import type { CampanhaTemplateProps } from 'src/components/cms/templates/CampanhaTemplate'
import { resolveRenderTemplate } from 'src/utils/cms/resolveRenderTemplate'
import type { RenderTemplate } from 'src/utils/cms/resolveRenderTemplate'
import { getCollectionTemplateServerData } from 'src/utils/plp/getCollectionTemplateServerData'
import { getCampanhaTemplateServerData } from 'src/utils/getCampanhaTemplateServerData'

type ServerData = {
  contentBrand: any
}

export type Props = PageProps<
  SearchPageQueryQuery,
  SearchPageQueryQueryVariables,
  ServerData
>

interface PropsCMS extends Props {
  slug: string
  serverData: {
    contentBrand?: ContentBrand
    searchParams: SearchState
    searchResult: SearchResult
    renderTemplate?: RenderTemplate
  }
}

interface ContentBrand {
  status: any
  props: CmsData
  headers: any
}
interface CmsData {
  cmsData: Sections
}

interface Sections {
  sections: Array<{
    data: {
      items: {
        sectionA: {
          brandName: string
          brandType: string
          brandSEOTitle: string
          brandSEODescription: string
          brandSEOKeywords: string
          brandLogo: string
        }
        sectionB: any
        sectionC: any
      }
    }
    id: string
    name: string
  }>
  status: string
}

function Page(props: PropsCMS) {
  const {
    serverData,
    data: { site },
  } = props

  if (serverData === null) {
    return null
  }

  const { searchParams, searchResult, contentBrand, renderTemplate } =
    serverData

  if (renderTemplate === 'collection-plp') {
    return (
      <CollectionPLPTemplate
        serverData={
          serverData as unknown as CollectionPLPTemplateProps['serverData']
        }
        location={props.location}
        data={{ site }}
      />
    )
  }

  if (renderTemplate === 'campanha') {
    return (
      <CampanhaTemplate
        serverData={
          serverData as unknown as CampanhaTemplateProps['serverData']
        }
        location={props.location}
        data={{ site }}
      />
    )
  }

  const cmsData = contentBrand?.props?.cmsData
  const { pathname, search } = props.location

  const brand = cmsData?.sections[0]?.data?.items?.sectionA?.brandName

  const substituteTitle = `${brand} | Decathlon`
  const title =
    cmsData?.sections[0]?.data?.items?.sectionA?.brandSEOTitle ??
    substituteTitle

  const substituteDescription = `${brand}, a Decathlon online é a maior loja de esportes com produtos fitness, de musculação, ciclismo, futebol, corrida, camping, trilha, natação e mais. Confira!`
  const description =
    cmsData?.sections[0]?.data?.items?.sectionA?.brandSEODescription ??
    substituteDescription

  const keywords = cmsData?.sections[0]?.data?.items?.sectionA?.brandSEOKeywords

  const dataBrand = cmsData?.sections[0]?.data?.items
  const branchPictos = dataBrand?.sectionC?.categoriesBrand
  const hasPictosOnSections = Boolean(branchPictos?.length)

  if (!searchParams) {
    return null
  }

  return (
    <SearchProvider
      onChange={applySearchState}
      itemsPerPage={ITEMS_PER_PAGE}
      {...searchParams}
    >
      {/* SEO */}
      <Helmet>
        <meta name="keywords" content={keywords} />
        <meta
          property="og:image"
          content={cmsData?.sections[0].data.items.sectionA.brandLogo}
        />
      </Helmet>
      <GatsbySeo
        language="pt-br"
        title={title}
        description={description}
        canonical={`${site?.siteMetadata?.siteUrl}${pathname}${search}`}
        openGraph={{
          images: [{ url: site?.siteMetadata?.ogImage ?? '' }],
          url: `${site?.siteMetadata?.siteUrl}${pathname}${search}`,
          title,
          description,
        }}
      />

      {/* MAIN BANNER MARCA  */}
      <MainBannerBrands data={cmsData?.sections[0]?.data?.items?.sectionA} />

      {/* SECTION BRAND */}
      <SectionBrand data={cmsData} />

      {/* VITRINE */}
      <ProductGallery
        dataBrand={cmsData}
        title="Marcas"
        type="search"
        searchResult={searchResult}
        hasPictosOnSections={hasPictosOnSections}
      />
      <MusePlpEntryPoint />
    </SearchProvider>
  )
}

export const querySSG = graphql`
  query BrandPage {
    site {
      ...SiteInformation
    }
  }
`
export const getServerData = async ({
  params: { slug },
  query,
}: {
  query: Record<string, string>
  params: Record<string, string>
}) => {
  const CACHE_CONTROL = `max-age=300, s-maxage=7200, stale-while-revalidate`

  let currentStep = 'initializing'

  try {
    currentStep = 'getCMSPageByContentType'
    const contentBrand = await getCMSPageByContentType({
      slug,
      contentType: 'marcas',
      pageId: query?.pageId ?? '',
      versionId: query?.versionId ?? '',
    })

    const renderTemplate = resolveRenderTemplate(
      contentBrand.props?.cmsData ?? null
    )

    if (renderTemplate === 'collection-plp') {
      currentStep = 'getCollectionTemplateServerData'
      const result = await getCollectionTemplateServerData({
        slug,
        query,
        routePrefix: 'marcas',
        cmsData: contentBrand.props?.cmsData ?? null,
      })

      return { ...result, props: { ...result.props, renderTemplate } }
    }

    if (renderTemplate === 'campanha') {
      currentStep = 'getCampanhaTemplateServerData'
      const result = await getCampanhaTemplateServerData({
        slug,
        query,
        cmsData: contentBrand,
      })

      return { ...result, props: { ...result.props, renderTemplate } }
    }

    if (contentBrand.status === 301 || !contentBrand?.props?.cmsData) {
      return {
        status: 301,
        props: {},
        headers: {
          'cache-control': CACHE_CONTROL,
          location: `/pesquisa/?q=marca%20${slug.replace('-', '%20')}`, // search does't always work with "-", so i'm using "%20"
        },
      }
    }

    currentStep = 'getSearchServerSidedata'
    const { searchParams, searchResult } = await getSearchServerSidedata({
      query,
      basePath: `marcas/${slug}`,
      searchTerm: null,
      slug,
    })

    return {
      status: 200,
      props: {
        contentBrand,
        slug,
        searchParams,
        searchResult,
        pageType: 'Marcas',
      },
      headers: {
        'cache-control': CACHE_CONTROL,
      },
    }
  } catch (err) {
    handleSSRError({
      err: err as Error,
      query,
      slug: String(slug ?? '/marcas'),
      stepName: currentStep,
    })

    return {
      status: 500,
      props: {},
      headers: {
        'cache-control': 'public, max-age=0, must-revalidate',
      },
    }
  }
}

Page.displayName = 'Page'
export default mark(Page)
